import { useState, useEffect, useCallback, useRef } from "react";
import { useNavigate } from "react-router-dom";
import { X, CheckCheck, Loader2 } from "lucide-react";
import Icon from "./Icon.jsx";
import { api } from "../lib/api.js";
import { useSmartPoll } from "../lib/useSmartPoll.js";
import EmptyState from "./EmptyState.jsx";

// v8.5.49 · piel · agrupa por HOY / ESTA SEMANA / ANTES — misma fecha de
// created_at que ya usa relativeTime(), solo se le pone un balde encima.
function agruparPorFecha(items) {
  const hoy = new Date(); hoy.setHours(0, 0, 0, 0);
  const haceUnaSemana = new Date(hoy); haceUnaSemana.setDate(hoy.getDate() - 7);
  const grupos = { "Hoy": [], "Esta semana": [], "Antes": [] };
  for (const n of items) {
    const d = new Date(n.created_at);
    if (d >= hoy) grupos["Hoy"].push(n);
    else if (d >= haceUnaSemana) grupos["Esta semana"].push(n);
    else grupos["Antes"].push(n);
  }
  return Object.entries(grupos).filter(([, arr]) => arr.length > 0);
}

export default function NotifBell({ circle = false }) {
  const navigate = useNavigate();
  const [open, setOpen] = useState(false);
  const [items, setItems] = useState([]);
  const [noLeidas, setNoLeidas] = useState(0);
  const [loading, setLoading] = useState(false);
  const dropdownRef = useRef(null);

  const cargar = useCallback(async () => {
    try {
      const r = await api.notifs({ limit: 30 });
      setItems(r.notifs || []);
      setNoLeidas(r.no_leidas || 0);
    } catch {}
  }, []);

  // Cargar inicial + smart poll cada 45s (con pausa si tab oculta)
  useEffect(() => { cargar(); }, [cargar]);
  useSmartPoll(cargar, 45_000);

  // Cerrar al click fuera
  useEffect(() => {
    if (!open) return;
    const handler = (e) => {
      if (dropdownRef.current && !dropdownRef.current.contains(e.target)) setOpen(false);
    };
    document.addEventListener("mousedown", handler);
    return () => document.removeEventListener("mousedown", handler);
  }, [open]);

  const clickNotif = async (n) => {
    if (!n.leido) {
      try { await api.notifLeida(n.id); } catch {}
    }
    setOpen(false);
    if (n.url) navigate(n.url);
    cargar();
  };

  const markAll = async () => {
    setLoading(true);
    try { await api.notifMarkAll(); await cargar(); }
    finally { setLoading(false); }
  };

  const eliminar = async (id, e) => {
    e.stopPropagation();
    try { await api.notifEliminar(id); cargar(); } catch {}
  };

  const grupos = agruparPorFecha(items);

  return (
    <div className="relative" ref={dropdownRef}>
      {/* v8.5.49 · piel · ícono 16px; el contador numérico se cae, queda solo
          un punto de 6px --danger cuando hay algo sin leer. */}
      <button onClick={() => setOpen(o => !o)}
              className={`relative flex items-center justify-center transition ${
                circle
                  ? "w-[46px] h-[46px] rounded-full bg-[var(--surface)] hover:bg-[var(--line)]"
                  : "w-11 h-11 rounded-full hover:bg-[var(--line)]"
              }`}
              aria-label="Notificaciones">
        <Icon name="notifications" size={circle ? 22 : 24} className="text-[var(--ink)]"/>
        {noLeidas > 0 && (
          <span className="absolute top-2.5 right-2.5 w-2 h-2 bg-[var(--notif)] rounded-full ring-2 ring-[var(--surface)]"/>
        )}
      </button>

      {/* v8.5.68 · fix auditoría móvil · en mobile la campana no es el
          último ícono de la barra (después va "Más"), así que anclar el
          panel con `right-0` a la propia campana lo hacía crecer hacia la
          izquierda y salirse por el borde del viewport. `fixed` + inset-x
          lo ancla a la pantalla en mobile; en desktop sigue `absolute`
          pegado a la campana como antes. */}
      {open && (
        <div className="shadow-overlay fixed inset-x-3 top-16 sm:absolute sm:inset-x-auto sm:right-0 sm:top-14 sm:w-[380px] bg-[var(--raised)] rounded-[20px] overflow-hidden z-50">
          {/* Header */}
          <div className="px-4 py-3 border-b border-[var(--line)]">
            <h3 className="font-semibold text-sm text-[var(--ink)]">Notificaciones</h3>
            {noLeidas > 0 && (
              <div className="text-[13px] text-[var(--muted)]">{noLeidas} sin leer</div>
            )}
          </div>

          {/* Lista, agrupada por HOY / ESTA SEMANA / ANTES */}
          <div className="max-h-[500px] overflow-y-auto">
            {items.length === 0 ? (
              <EmptyState size="sm" label="Notificaciones" title="Sin notificaciones" description="Te avisamos cuando haya algo nuevo"/>
            ) : (
              grupos.map(([label, arr]) => (
                <div key={label}>
                  <div className="font-mono text-[12px] font-medium uppercase tracking-[.08em] text-[var(--faint)] px-4 pt-3 pb-1">
                    {label}
                  </div>
                  {arr.map(n => <NotifItem key={n.id} n={n} onClick={() => clickNotif(n)} onDelete={(e) => eliminar(n.id, e)}/>)}
                </div>
              ))
            )}
          </div>

          {/* Pie — botón fantasma */}
          {noLeidas > 0 && (
            <div className="px-4 py-2.5 border-t border-[var(--line)]">
              <button onClick={markAll} disabled={loading}
                      className="text-[15px] text-[var(--accent)] hover:underline flex items-center gap-1.5">
                {loading ? <Loader2 className="h-3.5 w-3.5 animate-spin"/> : <CheckCheck className="h-3.5 w-3.5"/>}
                Marcar todas como leídas
              </button>
            </div>
          )}
        </div>
      )}
    </div>
  );
}

function NotifItem({ n, onClick, onDelete }) {
  const ago = relativeTime(n.created_at);
  return (
    <button onClick={onClick}
            className="w-full text-left px-4 py-3 flex gap-3 hover:bg-[var(--panel)] transition border-b border-[var(--line)]">
      {/* v8.5.49 · piel · punto --accent de 6px a la izquierda si no fue leída
          (antes era un fondo primary/5 en toda la fila) */}
      <span className={`w-1.5 h-1.5 rounded-full flex-shrink-0 mt-1.5 ${!n.leido ? "bg-[var(--accent)]" : ""}`}/>
      <div className="flex-1 min-w-0">
        <div className={`text-[15px] ${!n.leido ? "font-semibold text-[var(--ink)]" : "text-[var(--ink-2)]"}`}>
          {n.titulo}
        </div>
        {n.cuerpo && <div className="text-[13px] text-[var(--muted)] mt-0.5 line-clamp-2">{n.cuerpo}</div>}
        <div className="font-mono text-[12px] text-[var(--faint)] mt-1">{ago}</div>
      </div>
      <button onClick={onDelete}
              className="text-[var(--faint)] hover:text-[var(--danger)] self-start"
              aria-label="Eliminar">
        <X className="h-3 w-3"/>
      </button>
    </button>
  );
}

function relativeTime(iso) {
  if (!iso) return "";
  const d = new Date(iso);
  const diff = Date.now() - d.getTime();
  const min = Math.floor(diff / 60000);
  if (min < 1) return "ahora";
  if (min < 60) return `hace ${min} min`;
  const hrs = Math.floor(min / 60);
  if (hrs < 24) return `hace ${hrs}h`;
  const dias = Math.floor(hrs / 24);
  if (dias < 7) return `hace ${dias}d`;
  return d.toLocaleDateString("es-PY", { day: "2-digit", month: "short" });
}
